import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { sessionService } from '../../services/auth/sessionService';
import { authQueryKeys } from './queryKeys';
import type { User } from '../../types/auth';

export function useAuth() {
  const queryClient = useQueryClient();

  console.log('[useAuth] Hook called');

  const { data: user, isLoading, error } = useQuery({
    queryKey: authQueryKeys.user(),
    queryFn: () => {
      console.log('[useAuth] QueryFn executing - fetching user data...');
      return new Promise<User | null>((resolve) => {
        // Сначала проверяем, есть ли уже данные в кэше - это ускорит загрузку
        const cachedUser = queryClient.getQueryData<User>(authQueryKeys.user());
        if (cachedUser) {
          console.log('[useAuth] Found user in cache:', cachedUser.email);
          resolve(cachedUser);
          return;
        }
        
        // Также проверяем getCurrentUser() как быстрый fallback
        const currentUser = sessionService.getCurrentUser();
        if (currentUser) {
          console.log('[useAuth] Found user via getCurrentUser():', currentUser.email);
          resolve(currentUser);
          return;
        }
        
        // Таймаут 5 секунд чтобы избежать бесконечной загрузки
        const timeoutId = setTimeout(() => {
          console.warn('[useAuth] QueryFn timeout - using getCurrentUser as fallback');
          const fallbackUser = sessionService.getCurrentUser();
          if (fallbackUser) {
            console.log('[useAuth] Found user via getCurrentUser fallback:', fallbackUser.email);
            resolve(fallbackUser);
          } else {
            console.warn('[useAuth] QueryFn timeout - resolving with null');
            resolve(null);
          }
        }, 5000);
        
        const unsubscribe = sessionService.onAuthStateChanged((userData) => {
          console.log('[useAuth] onAuthStateChanged received data:', {
            hasUser: !!userData,
            email: userData?.email,
            emailVerified: userData?.emailVerified,
            role: userData?.role,
            id: userData?.id
          });
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(userData);
        });
      });
    },
    staleTime: Infinity,
    gcTime: Infinity
  });

  // Subscribe to auth state changes
  useEffect(() => {
    console.log('[useAuth] Setting up auth state listener');
    const unsubscribe = sessionService.onAuthStateChanged((newUser) => {
      console.log('[useAuth] Auth state changed:', {
        hasUser: !!newUser,
        email: newUser?.email,
        emailVerified: newUser?.emailVerified,
        role: newUser?.role
      });
      queryClient.setQueryData(authQueryKeys.user(), newUser);
    });

    return () => {
      console.log('[useAuth] Cleaning up auth state listener');
      unsubscribe();
    };
  }, [queryClient]);

  // DEBUG: Log when user changes to null (this causes dashboard empty state)
  const [prevUser, setPrevUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    if (prevUser !== undefined && prevUser !== null && user === null) {
      console.log('[useAuth] ⚠️ CRITICAL: User changed from', prevUser?.email, 'to null!');
      console.log('[useAuth] This causes Dashboard to render empty!');
    }
    setPrevUser(user);
  }, [user, prevUser]);

  const result = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    needsRoleSelection: user?.role === 'pending',
    isEmailVerified: user?.emailVerified ?? false
  };

  console.log('[useAuth] Returning:', {
    isLoading,
    isAuthenticated: result.isAuthenticated,
    isEmailVerified: result.isEmailVerified,
    needsRoleSelection: result.needsRoleSelection,
    userEmail: user?.email,
    userRole: user?.role
  });

  return result;
}
